import styled, { css, createGlobalStyle, keyframes } from 'styled-components';
import { colors } from '@/utils/colorSystem';
import { ContentCard } from '@/pages/inside/tenant/style';

/**
 * Project module: board shell, column chrome, tab rows (project tabs only).
 * Import from here for new code; `style.js` re-exports for backward compatibility.
 */

/** Flex column shell for board tabs (replaces duplicate wrap layouts). */
const BoardTabRootBase = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;
export const BoardTabRoot = BoardTabRootBase;
/** @deprecated Use BoardTabRoot */
export const SprintBoardWrap = BoardTabRootBase;
/** @deprecated Use BoardTabRoot */
export const SprintsTabLayout = BoardTabRootBase;

/**
 * Host for tab content on pages whose body is a plain block (e.g. the Projects directory).
 * Board tabs are `flex: 1; min-height: 0`, so without a sized flex parent they collapse to
 * zero height and the tab renders blank.
 */
export const TabContentFrame = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    min-height: ${({ $minHeight }) => $minHeight ?? 'clamp(420px, calc(100vh - 300px), 1200px)'};
`;

/**
 * Covers tab content while a newly selected tab loads, so no blank frame shows through.
 * The frame is as tall as the tallest board, so the loader is pinned near the top rather
 * than centred, which would push it below the fold.
 */
export const TabContentLoadingCover = styled.div`
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: clamp(32px, 9vh, 88px);
    background: #ffffff;
`;

/**
 * Extends Kanban to the viewport right edge. Set `--board-kanban-inline-gutter` on `SprintBoardWrap`
 * to the same value as its horizontal margin (e.g. `clamp(16px, 4vw, 40px)` or `40px`).
 */
export const BoardKanbanBleedRight = styled.div`
    width: calc(100% + var(--board-kanban-inline-gutter, clamp(16px, 4vw, 40px)));
    margin-right: calc(-1 * var(--board-kanban-inline-gutter, clamp(16px, 4vw, 40px)));
    min-width: 0;
    box-sizing: border-box;
`;

/**
 * Same bleed as BoardKanbanBleedRight, but on narrow viewports keeps width flush to the content column
 * (no negative margin) so the board does not extend past the viewport.
 */
export const BoardKanbanBleedRightFlushNarrow = styled(BoardKanbanBleedRight)`
    @media (max-width: 1024px) {
        width: 100%;
        max-width: 100%;
        margin-right: 0;
    }
`;

/**
 * Kanban area that stays inside the tab horizontal padding (same inset as title/actions).
 * Use instead of {@link BoardKanbanBleedRight} when the board should not extend past the right gutter.
 */
export const BoardKanbanBoardShell = styled.div`
    flex: 1;
    min-height: 0;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-bottom: 0;
    padding-bottom: 0;
`;

const boardKanbanShimmerMove = keyframes`
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
`;

/** Indeterminate strip below sprint tabs while sprint detail / kanban data is loading. */
export const BoardKanbanFetchingStrip = styled.div`
    height: 3px;
    border-radius: 999px;
    margin-top: 2px;
    margin-bottom: 10px;
    flex-shrink: 0;
    width: 100%;
    max-width: 100%;
    background: linear-gradient(
        90deg,
        var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.04)) 0%,
        var(--ant-color-primary, #7c3aed) 50%,
        var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.04)) 100%
    );
    background-size: 200% 100%;
    animation: ${boardKanbanShimmerMove} 1.1s ease-in-out infinite;
    opacity: 0.65;
`;

/** Placeholder row matching three-column kanban while sprint-by-id is in flight. */
export const BoardKanbanShimmerRoot = styled.div`
    flex: 1;
    min-height: 280px;
    display: flex;
    gap: 16px;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    box-sizing: border-box;
`;

export const BoardKanbanShimmerColumn = styled.div`
    flex: 1 1 0%;
    min-width: 0;
    border-radius: 12px;
    border: 1px solid
        ${({ theme }) =>
            theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'};
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const BoardKanbanShimmerBlock = styled.div`
    height: ${({ $height }) => ($height != null ? $height : 48)}px;
    border-radius: 10px;
    background: linear-gradient(
        110deg,
        ${({ theme }) =>
                theme.colors.background.default === '#141414'
                    ? 'rgba(255, 255, 255, 0.04)'
                    : 'rgba(0, 0, 0, 0.05)'}
            0%,
        ${({ theme }) =>
                theme.colors.background.default === '#141414'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)'}
            45%,
        ${({ theme }) =>
                theme.colors.background.default === '#141414'
                    ? 'rgba(255, 255, 255, 0.04)'
                    : 'rgba(0, 0, 0, 0.05)'}
            90%
    );
    background-size: 220% 100%;
    animation: ${boardKanbanShimmerMove} 1.35s ease-in-out infinite;
`;

/** Desktop Kanban: columns share space equally; below this width the row overflows and scrolls horizontally. */
export const KANBAN_DESKTOP_COLUMN_MIN_PX = 340;

/** Header cell above each column — matches {@link KanbanColumnPanel} flex basis for scroll sync. */
export const kanbanDesktopHeaderCellFlexStyle = {
    flex: '1 1 0%',
    minWidth: KANBAN_DESKTOP_COLUMN_MIN_PX,
    width: 0,
};

/** Inner flex row for desktop Kanban body (synced horizontal scroll). */
export const kanbanDesktopBoardRowInnerStyle = {
    display: 'flex',
    gap: 16,
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    width: 'max-content',
    minWidth: '100%',
    paddingRight: 30,
};

/** Inner flex row for desktop Kanban sticky header strip (same width behavior as body row). */
export const kanbanDesktopBoardHeaderRowInnerStyle = {
    display: 'flex',
    gap: 16,
    flexWrap: 'nowrap',
    width: 'max-content',
    minWidth: '100%',
    paddingRight: 30,
};

/** Blockers (and similar): fixed column width + max-content row — no equal flex stretch on wide screens. */
export const kanbanDesktopFixedHeaderCellStyle = {
    flex: `0 0 ${KANBAN_DESKTOP_COLUMN_MIN_PX}px`,
    width: KANBAN_DESKTOP_COLUMN_MIN_PX,
    minWidth: KANBAN_DESKTOP_COLUMN_MIN_PX,
    maxWidth: KANBAN_DESKTOP_COLUMN_MIN_PX,
};

export const kanbanDesktopFixedBoardRowInnerStyle = {
    display: 'flex',
    gap: 16,
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    width: 'max-content',
    minWidth: '100%',
};

export const kanbanDesktopFixedBoardHeaderRowInnerStyle = {
    display: 'flex',
    gap: 16,
    flexWrap: 'nowrap',
    width: 'max-content',
    minWidth: '100%',
};

export const SprintBoardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
`;
export const SprintBoardSearch = styled.input`
    flex: 1;
    min-width: 180px;
    max-width: 280px;
    padding: 8px 14px 8px 36px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#e8e8e8')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#fff')};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#1a1a1a')};
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
    &:focus {
        border-color: var(--ant-color-primary);
    }
    &::placeholder {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#8c8c8c')};
    }
`;
export const SprintBoardToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;
export const SprintBoardColumns = styled.div`
    display: flex;
    gap: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0;
    min-height: 0;
    flex: 1;
    align-items: stretch;
    max-height: 100%;
    &::-webkit-scrollbar {
        height: 8px;
    }
    &::-webkit-scrollbar-track {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f5f5f5')};
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d0d0d0')};
        border-radius: 4px;
    }
`;
/** Row of sprint headers only (2nd header) — same flex/width as SprintBoardColumns so columns align with body */
export const SprintHeadersRow = styled.div`
    display: flex;
    gap: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0;
    margin-bottom: 8px;
    min-height: 0;
    flex-shrink: 0;
    &::-webkit-scrollbar {
        height: 8px;
    }
    &::-webkit-scrollbar-track {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f5f5f5')};
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d0d0d0')};
        border-radius: 4px;
    }
`;
/** One cell in SprintHeadersRow — same width as SprintBoardColumn so headers align with task columns */
export const SprintHeaderCell = styled.div`
    flex: 0 0 340px;
    min-width: 340px;
`;
export const SprintBoardColumn = styled.div`
    flex: 0 0 340px;
    min-width: 340px;
    height: 100%;
    min-height: 0;
    border-radius: 12px;
    background: ${({ $columnBg, theme }) => $columnBg || (theme.colors.background.default === '#141414' ? '#252528' : '#f8fafc')};
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    ${({ $columnDisabled }) =>
        $columnDisabled &&
        `
        opacity: 0.6;
        cursor: not-allowed;
    `}
`;

/** Page heading inside a tab (e.g. Project Sprints) — matches compact dashboard typography. */
export const BoardPageTitle = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.02em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f9fafb' : '#111827')};
    font-family: var(--font-family-geist);
`;

export const BoardPageSubtitle = styled.p`
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.45;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#6b7280')};
    font-family: var(--font-family-geist);
`;

/** Segmented milestone switcher — light grey track, vertical dividers, darker grey selected fill (full segment). */
export const BoardPillTabRow = styled.div`
    display: inline-flex;
    align-items: stretch;
    flex-wrap: nowrap;
    width: fit-content;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    border-radius: 12px;
    padding: 0;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.1)' : '#e5e7eb')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#f0f0f0')};
    box-sizing: border-box;
    scrollbar-width: thin;
`;

/** Milestone label — centered; ellipsis when text exceeds tab width. */
export const BoardPillTabLabel = styled.span`
    display: block;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
`;

export const BoardPillTab = styled.button`
    box-sizing: border-box;
    flex: 0 0 140px;
    flex-grow: 0;
    flex-shrink: 0;
    width: 140px;
    min-width: 140px;
    max-width: 140px;
    margin: 0;
    border: none;
    border-radius: 0;
    cursor: pointer;
    padding: 10px 8px;
    overflow: hidden;
    font-size: 14px;
    font-family: var(--font-family-geist);
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        background 0.18s ease,
        color 0.18s ease;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    border-right: 1px solid
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.1)' : '#e5e7eb')};
    &:last-child {
        border-right: none;
    }
    background: ${({ $active, theme }) =>
        $active
            ? theme.colors.background.default === '#141414'
                ? '#3a3a3a'
                : '#e8e8e8'
            : 'transparent'};
    color: ${({ $active, theme }) =>
        $active
            ? theme.colors.background.default === '#141414'
                ? '#f9fafb'
                : '#111827'
            : theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.45)'
                : '#9ca3af'};
    &:hover {
        ${({ $active, $disabled, theme }) =>
        $disabled
            ? ''
            : `
        color: ${$active
                ? theme.colors.background.default === '#141414'
                    ? '#f9fafb'
                    : '#111827'
                : theme.colors.background.default === '#141414'
                    ? 'rgba(255,255,255,0.75)'
                    : '#4b5563'
            };
        background: ${$active
                ? theme.colors.background.default === '#141414'
                    ? '#3a3a3a'
                    : '#e8e8e8'
                : theme.colors.background.default === '#141414'
                    ? 'rgba(255,255,255,0.08)'
                    : '#fafafa'
            };
    `}
    }
    ${({ $disabled }) =>
        $disabled &&
        `
        cursor: not-allowed;
        opacity: 0.55;
        pointer-events: none;
    `}
`;

/** Collapse section title (Sprint Details / Tasks) — 18px semibold. */
export const BoardCollapseSectionHeading = styled.span`
    font-size: 18px;
    font-weight: 600;
    line-height: 1.35;
    font-family: var(--font-family-geist);
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f3f4f6' : '#111827')};
`;

/** Sprint board: sprint detail block title (sprint name). */
export const BoardDetailBlockTitle = styled.h2`
    font-size: 16px;
    font-weight: 500;
    margin: 0 0 20px 0;
    line-height: 1.3;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f3f4f6' : '#000000')};
    font-family: var(--font-family-geist);
`;

/** Sprint board: description area (muted body). */
export const BoardDetailBlockBody = styled.div`
    font-size: 14px;
    line-height: 1.55;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.65)' : '#717171')};
    font-family: var(--font-family-geist);
    margin-bottom: 28px;
`;

/** Key label for sprint detail rows (e.g. "Sprint Deadline :") — regular weight. */
export const BoardDetailKeyLabel = styled.span`
    flex: 0 0 168px;
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f3f4f6' : '#000000')};
    font-family: var(--font-family-geist);
    line-height: 1.5;
`;

export const BoardDetailMetaStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const BoardDetailMetaRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
`;

export const BoardDetailMetaValue = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 400;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f3f4f6' : '#000000')};
    font-family: var(--font-family-geist);
`;


/** Full-width underline; tabs sit inset via inner padding so the line extends past the tab row. */
export const BoardUnderlineTabRow = styled.div`
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0;
    border-bottom: 1px solid
        ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : '#e5e7eb')};
    margin-top: 0;
    /* Pinned under PageBodyScroll top so Kanban column headers can stack below (see --sprint-kanban-sticky-top) */
    ${({ $sticky, theme }) =>
        $sticky &&
        css`
            position: sticky;
            top: 0;
            z-index: 9;
            /* Breathing room from PageBodyScroll top when pinned (pairs with --sprint-kanban-sticky-top on Kanban) */
            padding-top: 8px;
            background: ${theme.colors.background.default === '#141414'
                ? theme.colors.background.secondary || '#1f1f1f'
                : '#ffffff'};
        `}
`;

/** Inner flex row for sprint tabs — horizontal scroll when many sprints; fixed tab width. */
export const BoardUnderlineTabInner = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0 8px 0px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

/** Title + percent on one row; name truncates with ellipsis when space is tight. */
export const BoardUnderlineTabLine = styled.span`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 6px;
    width: 100%;
    min-width: 0;
    text-align: left;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    color: inherit;

    .sprint-tab-info-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: ${({ theme }) =>
            theme.colors.background.default === '#141414'
                ? 'rgba(255, 255, 255, 0.45)'
                : 'rgba(0, 0, 0, 0.45)'};
        transition: color 0.15s;

        &:hover {
            color: ${({ theme }) => theme.colors?.primary || '#7543DC'};
        }
    }
`;

export const BoardUnderlineTabPrimaryLabel = styled.span`
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ $active, theme }) =>
        $active
            ? theme.colors.background.default === '#141414'
                ? '#f9fafb'
                : '#111827'
            : theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.45)'
                : '#9ca3af'};
`;

/** @deprecated Use BoardUnderlineTabLine — kept for any older imports */
export const BoardUnderlineTabLabel = styled.span`
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const BoardUnderlineTab = styled.button`
    box-sizing: border-box;
    position: relative;
    border: none;
    background: transparent;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    padding: 0 4px 12px;
    margin-bottom: -1px;
    flex: 0 0 160px;
    flex-grow: 0;
    flex-shrink: 0;
    width: 160px;
    min-width: 160px;
    max-width: 160px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    min-height: 0;
    overflow: hidden;
    text-align: center;
    font-family: var(--font-family-geist);
    font-size: 16px;
    font-weight: 600;
    color: transparent;
    border-bottom: 3px solid
        ${({ $active, $disabled, theme }) =>
        $disabled ? 'transparent' : $active ? theme.colors?.primary || '#7543DC' : 'transparent'};
    transition: border-color 0.2s;
    ${({ $disabled }) =>
        $disabled &&
        `
        opacity: 0.55;
        pointer-events: none;
    `}
`;

export const BoardUnderlineTabPercent = styled.span`
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 16px;
    font-weight: 600;
    font-family: var(--font-family-geist);
    color: ${({ $active, theme }) =>
        $active
            ? theme.colors?.primary || '#7543DC'
            : theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.45)'
                : '#9ca3af'};
`;

/**
 * Compact stat pill for underline tabs (e.g. QA sprint task count).
 * Tinted primary fill + border; numerals use saturated primary blend for readability.
 */
export const BoardUnderlineTabCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 22px;
    padding: 0 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-family: var(--font-family-geist);
    flex-shrink: 0;
    line-height: 1;
    letter-spacing: 0.02em;
    box-sizing: border-box;
    border: 1px solid
        ${({ theme, $active }) => {
        const p = theme.colors?.primary || '#7543DC';
        const a = $active ? 0.42 : 0.28;
        return `color-mix(in srgb, ${p} ${Math.round(a * 100)}%, transparent)`;
    }};
    background: ${({ theme, $active }) => {
        const p = theme.colors?.primary || '#7543DC';
        const a = $active ? 0.22 : 0.14;
        return `color-mix(in srgb, ${p} ${Math.round(a * 100)}%, transparent)`;
    }};
    color: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? `color-mix(in srgb, ${theme.colors?.primary || '#7543DC'} 88%, #ffffff)`
            : `color-mix(in srgb, ${theme.colors?.primary || '#7543DC'} 72%, #1e1b4b)`};
    ${({ $disabled, theme }) =>
        $disabled &&
        `
        border-color: ${theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)'};
        background: ${theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
        color: ${theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : 'rgba(75,85,99,0.92)'};
    `}
`;

/** @deprecated Use BoardUnderlineTabPrimaryLabel */
export const BoardUnderlineTabSprintName = BoardUnderlineTabPrimaryLabel;

/** PMO / QA: one grouped submission card (SID + title + nested task rows). */
export const BoardGroupedSubmissionCard = styled.div`
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.1)' : '#e5e7eb')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#ffffff')};
    padding: 12px 12px 10px;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

export const BoardGroupedSubmissionSid = styled.span`
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.45)' : '#6b7280')};
    font-family: var(--font-family-geist);
    margin-bottom: 4px;
`;

export const BoardGroupedSubmissionHeading = styled.span`
    display: block;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#f3f4f6' : '#111827')};
    font-family: var(--font-family-geist);
`;

export const BoardGroupedSubmissionRule = styled.div`
    height: 0;
    margin: 10px 0 8px;
    border: none;
    border-top: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : '#e5e7eb')};
`;

export const BoardGroupedSubmissionTaskRow = styled.button`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    margin: 0;
    padding: 8px 10px;
    border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : '#e5e7eb')};
    border-radius: 10px;
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#262626' : '#f9fafb')};
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: inherit;
    transition: background 0.15s ease;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2d2d2d' : '#f3f4f6')};
    }
`;

export const BoardGroupedSubmissionTaskId = styled.span`
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    font-family: var(--font-family-geist);
    color: ${colors.neutral[500]};
    max-width: 38%;
`;

export const BoardGroupedSubmissionTaskPreview = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 13px;
    line-height: 1.4;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.82)' : '#374151')};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

/**
 * Field labels in project modals (Title, Priority, Description, etc.) — same as Submission Description in {@link ProjectSubmissionModal}.
 */
export const submissionRichDescriptionLabelStyle = {
    display: 'block',
    marginBottom: 4,
    color: '#71717A',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.25,
};

/** Extra space above the description block (after name/dates or title row). */
export const submissionRichDescriptionFormItemStyle = {
    marginTop: 8,
};

/** Tighter label → control and field spacing for create sprint / create task modals (same rhythm). */
export const projectSprintTaskModalFormStyles = {
    item: { marginBottom: 8 },
    label: { paddingBottom: 0 },
};

/** Glass-style backdrop for Ant Design {@link Image} preview in phase attachment thumbnails (milestones tab). */
export const PhaseAttachmentImagePreviewGlobal = createGlobalStyle`
    .phase-attachment-image-preview-root .ant-image-preview-mask {
        backdrop-filter: blur(22px) saturate(1.25);
        -webkit-backdrop-filter: blur(22px) saturate(1.25);
        background: rgba(15, 23, 42, 0.55) !important;
    }
    .phase-attachment-image-preview-root .ant-image-preview-operations {
        background: rgba(255, 255, 255, 0.12) !important;
        backdrop-filter: blur(14px);
        -webkit-backdrop-filter: blur(14px);
        border: 1px solid rgba(255, 255, 255, 0.14);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    }
    .phase-attachment-image-preview-root .ant-image-preview-close {
        background: rgba(255, 255, 255, 0.12) !important;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.12);
    }
`;
